import React, { useEffect } from 'react';
import './SvgBackground.css';

// Define the dot interface
interface Dot {
  x: number;
  y: number;
  size: number;
  vx: number;
  vy: number;
  color: string;
  pulseSpeed: number;
  pulseDirection: number;
  pulseSize: number;
}

const SvgBackground: React.FC = () => {
  useEffect(() => {
    const createMysteriousDots = () => {
      // Clear any existing SVG
      const existingBackground = document.getElementById('mysteryDotsContainer');
      if (existingBackground) {
        existingBackground.remove();
      }

      // Create mysterious dots container
      const dotsContainer = document.createElement('div');
      dotsContainer.className = 'svg-background';
      dotsContainer.id = 'mysteryDotsContainer';
      dotsContainer.innerHTML = '<svg width="100%" height="100%" id="mysteryDots"></svg>';
      document.body.insertBefore(dotsContainer, document.body.firstChild);

      const mysteryDotsContainer = document.getElementById('mysteryDots');
      if (!mysteryDotsContainer) return;

      const svgNS = "http://www.w3.org/2000/svg";
      const width = window.innerWidth;
      const height = window.innerHeight;

      // Set SVG size
      mysteryDotsContainer.setAttribute('width', width.toString());
      mysteryDotsContainer.setAttribute('height', height.toString());

      // Create dots with proper typing
      const dotCount = Math.max(30, Math.min(120, Math.floor(width * height / 15000)));
      const dots: Dot[] = [];

      for (let i = 0; i < dotCount; i++) {
        const dot: Dot = {
          x: Math.random() * width,
          y: Math.random() * height,
          size: Math.random() * 3 + 1,
          vx: (Math.random() - 0.5) * 0.7,
          vy: (Math.random() - 0.5) * 0.7,
          color: i % 5 === 0 ? 'rgba(255, 183, 77, 0.6)' : 'rgba(77, 166, 255, 0.5)',
          pulseSpeed: Math.random() * 0.02 + 0.01,
          pulseDirection: Math.random() > 0.5 ? 1 : -1,
          pulseSize: 0
        };

        // Create the SVG circle
        const circle = document.createElementNS(svgNS, 'circle');
        circle.setAttribute('cx', dot.x.toString());
        circle.setAttribute('cy', dot.y.toString());
        circle.setAttribute('r', dot.size.toString());
        circle.setAttribute('fill', dot.color);
        circle.setAttribute('id', 'mystery-dot-' + i);

        // Add a subtle glow effect
        const glow = document.createElementNS(svgNS, 'filter');
        glow.setAttribute('id', 'glow-' + i);

        const feGaussianBlur = document.createElementNS(svgNS, 'feGaussianBlur');
        feGaussianBlur.setAttribute('stdDeviation', '2');
        feGaussianBlur.setAttribute('result', 'blur');
        glow.appendChild(feGaussianBlur);

        const feColorMatrix = document.createElementNS(svgNS, 'feColorMatrix');
        feColorMatrix.setAttribute('in', 'blur');
        feColorMatrix.setAttribute('type', 'matrix');
        feColorMatrix.setAttribute('values', '1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -7');
        feColorMatrix.setAttribute('result', 'glow');
        glow.appendChild(feColorMatrix);

        mysteryDotsContainer.appendChild(glow);
        circle.setAttribute('filter', 'url(#glow-' + i + ')');

        mysteryDotsContainer.appendChild(circle);
        dots.push(dot);
      }

      // Animation function
      function animateMysteriousDots() {
        for (let i = 0; i < dots.length; i++) {
          const dot = dots[i];
          const circle = document.getElementById('mystery-dot-' + i);

          if (circle) {
            // Move dot
            dot.x += dot.vx;
            dot.y += dot.vy;

            // Boundary check with bounce
            if (dot.x < 0 || dot.x > width) {
              dot.vx = -dot.vx;
              dot.x = Math.max(0, Math.min(width, dot.x));
            }
            if (dot.y < 0 || dot.y > height) {
              dot.vy = -dot.vy;
              dot.y = Math.max(0, Math.min(height, dot.y));
            }

            // Update position
            circle.setAttribute('cx', dot.x.toString());
            circle.setAttribute('cy', dot.y.toString());

            // Pulsing effect
            dot.pulseSize += dot.pulseSpeed * dot.pulseDirection;
            if (dot.pulseSize > 1 || dot.pulseSize < 0) {
              dot.pulseDirection *= -1;
            }

            const newSize = dot.size * (1 + dot.pulseSize * 0.5);
            circle.setAttribute('r', newSize.toString());

            // Occasionally change direction slightly
            if (Math.random() < 0.01) {
              dot.vx += (Math.random() - 0.5) * 0.2;
              dot.vy += (Math.random() - 0.5) * 0.2;

              // Limit maximum speed
              const speed = Math.sqrt(dot.vx * dot.vx + dot.vy * dot.vy);
              if (speed > 1) {
                dot.vx = (dot.vx / speed) * 1;
                dot.vy = (dot.vy / speed) * 1;
              }
            }

            // Occasionally connect to nearby dots
            if (Math.random() < 0.005 && i < dots.length - 1) {
              const nextDot = dots[i + 1];
              const dx = dot.x - nextDot.x;
              const dy = dot.y - nextDot.y;
              const distance = Math.sqrt(dx * dx + dy * dy);

              if (distance < 150) {
                const lineId = 'line-' + i + '-' + (i + 1);
                let line = document.getElementById(lineId) as SVGLineElement | null;

                if (!line) {
                  // Create new SVG line element
                  const newLine = document.createElementNS(svgNS, 'line') as SVGLineElement;
                  newLine.setAttribute('id', lineId);
                  newLine.setAttribute('stroke', dot.color);
                  newLine.setAttribute('stroke-width', '0.5');
                  newLine.setAttribute('opacity', '0.3');
                  mysteryDotsContainer?.appendChild(newLine);

                  line = newLine;
                }

                if (line) {
                  line.setAttribute('x1', dot.x.toString());
                  line.setAttribute('y1', dot.y.toString());
                  line.setAttribute('x2', nextDot.x.toString());
                  line.setAttribute('y2', nextDot.y.toString());
                }

                // Remove line after random time
                setTimeout(() => {
                  const oldLine = document.getElementById(lineId);
                  if (oldLine && mysteryDotsContainer) {
                    //mysteryDotsContainer.removeChild(oldLine);
                  }
                }, Math.random() * 5000 + 2000);
              }
            }
          }
        }

        requestAnimationFrame(animateMysteriousDots);
      }

      // Start animation
      animateMysteriousDots();
    };

    // Create the dots
    createMysteriousDots();

    // Handle window resize
    const handleResize = () => {
      createMysteriousDots();
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      const existingBackground = document.getElementById('mysteryDotsContainer');
      if (existingBackground) {
        existingBackground.remove();
      }
    };
  }, []);

  return null; // This component only adds the SVG background via JavaScript
};

export default SvgBackground;